import React from 'react';
import { Alert, Button } from 'antd';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { RouteGroupCard } from './RouteGroupCard';
import { GroupValidationAlerts } from './GroupValidationAlerts';
import { createEmptyDeliveryGroup } from './deliveryGroupModel';
import { validateDeliveryGroups } from './groupValidation';
import { ROUTE_UX } from './routeUxLabels';
import { RouteItineraryPanel } from './RouteItineraryPanel';
import { isSequentialStopsItineraryEditorEnabled } from './sequentialStopsItineraryFlags';
import { logItineraryEditorShadow } from './sequentialStopsItineraryShadow';
import { flattenGroupStops } from './executionLockContext';
import type { ItineraryCargoContext } from './itineraryStopSummary';
import './routeTopologyPanel.css';

type ApplicationRef = {
  id: number;
  internalNumber?: string | null;
  client?: { name?: string | null } | null;
  consigneeId?: number | null;
  loadingAddress?: string | null;
  loadingContactPerson?: string | null;
  loadingPhone?: string | null;
  unloadingAddress?: string | null;
  unloadingContactPerson?: string | null;
  unloadingPhone?: string | null;
  consignee?: { name?: string | null; contactPerson?: string | null; phone?: string | null; address?: string | null } | null;
};

type Props = {
  groups: DeliveryGroupDraft[];
  onChange: (next: DeliveryGroupDraft[]) => void;
  applications: ApplicationRef[];
  editingRouteId?: number;
  warehouses?: Array<{ id: number; name: string; city?: string; address?: string; contactPerson?: string; phone?: string }>;
  consignees?: Array<{ id: number; name: string; address?: string; contactPerson?: string; phone?: string; city?: string }>;
  makeStopUid: () => string;
  inTransit?: boolean;
  cargoContext?: ItineraryCargoContext;
  savePolicyError?: string | null;
  resolveCityHint?: (address: string) => Promise<string | null>;
  normalizeCity?: (city: string | null | undefined) => string | null;
  onGroupExecutionScopeClear?: (groupUid: string) => void;
};

export function RouteTopologyPanel({
  groups,
  onChange,
  applications,
  editingRouteId,
  warehouses,
  consignees,
  makeStopUid,
  inTransit,
  cargoContext,
  savePolicyError,
  resolveCityHint,
  normalizeCity,
  onGroupExecutionScopeClear
}: Props) {
  const issues = React.useMemo(() => validateDeliveryGroups(groups), [groups]);
  const itineraryEnabled = isSequentialStopsItineraryEditorEnabled();
  const allStops = React.useMemo(() => flattenGroupStops(groups), [groups]);

  const emitChange = React.useCallback(
    (next: DeliveryGroupDraft[]) => {
      if (itineraryEnabled) {
        logItineraryEditorShadow(next, 'topology-panel');
      }
      onChange(next);
    },
    [itineraryEnabled, onChange]
  );

  const updateGroup = (uid: string, patch: DeliveryGroupDraft) => {
    emitChange(groups.map((g) => (g.uid === uid ? patch : g)));
  };

  const addGroup = () => {
    emitChange([
      ...groups,
      createEmptyDeliveryGroup({
        sortOrder: groups.length,
        loadingPosition: groups.length,
        isDefault: false
      })
    ]);
  };

  const removeGroup = (uid: string) => {
    if (groups.length <= 1) return;
    emitChange(groups.filter((g) => g.uid !== uid));
  };

  const groupsColumn = (
    <div className="route-topology-panel__groups">
      {groups.map((group, index) => (
        <RouteGroupCard
          key={group.uid}
          group={group}
          allGroups={groups}
          groupIndex={index}
          applications={applications}
          editingRouteId={editingRouteId}
          warehouses={warehouses}
          consignees={consignees}
          makeStopUid={makeStopUid}
          inTransit={inTransit}
          allStops={allStops}
          resolveCityHint={resolveCityHint}
          normalizeCity={normalizeCity}
          onChange={(next) => updateGroup(group.uid, next)}
          onRemove={groups.length > 1 ? () => removeGroup(group.uid) : undefined}
          onGroupExecutionScopeClear={onGroupExecutionScopeClear}
        />
      ))}
      <Button type="dashed" onClick={addGroup}>
        + {ROUTE_UX.addGroup}
      </Button>
    </div>
  );

  const itineraryColumn = itineraryEnabled ? (
    <div className="route-topology-panel__itinerary">
      <RouteItineraryPanel
        groups={groups}
        onChange={emitChange}
        applications={applications}
        warehouses={warehouses}
        consignees={consignees}
        cargoContext={cargoContext}
        compact
      />
    </div>
  ) : null;

  return (
    <div className="route-topology-panel">
      {inTransit ? (
        <Alert type="warning" showIcon message={ROUTE_UX.inTransitWarning} description={ROUTE_UX.inTransitHint} style={{ marginBottom: 12 }} />
      ) : null}
      {savePolicyError ? (
        <Alert type="error" showIcon message={savePolicyError} style={{ marginBottom: 12 }} />
      ) : null}
      <GroupValidationAlerts issues={issues} />
      {itineraryEnabled ? (
        <div className="route-topology-panel__columns">
          {groupsColumn}
          {itineraryColumn}
        </div>
      ) : (
        groupsColumn
      )}
    </div>
  );
}
